import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { TextField } from "@/components/common/Forms";
import { IMAGES } from "@/data/site";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.contact_name || "back"}`);
      const next = params.get("next");
      const home = u.role === "admin" ? "/admin" : "/portal";
      navigate(next && next.startsWith(home) ? next : home, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="noise relative hidden overflow-hidden bg-[#123634] lg:block">
        <img src={IMAGES.technician} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#123634] via-[#123634]/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Logo dark testid="login-logo-link" />
          <div>
            <p className="eyebrow text-[#E8927F]">Vendor portal</p>
            <h1 className="mt-3 max-w-md text-4xl font-extrabold leading-tight text-white">AMC, warranty and service requests — tracked in one place.</h1>
            <p className="mt-4 max-w-md text-slate-300">Access is limited to verified businesses approved by the Vinsafe team.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden"><Logo /></div>
          <h2 className="mt-8 text-3xl font-bold text-slate-900 lg:mt-0">Sign in</h2>
          <p className="mt-2 text-slate-600">Approved vendors and Vinsafe administrators.</p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <TextField id="login-email" type="email" label="Email" value={email} onChange={setEmail} required autoComplete="email" />
            <TextField id="login-password" type="password" label="Password" value={password} onChange={setPassword} required autoComplete="current-password" />
            <div className="-mt-2 text-right"><Link to="/forgot-password" className="text-sm font-medium text-[#C20500] hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link></div>
            {error && <p className="rounded-md border border-red-200 bg-[#FBEDEA] p-3 text-sm text-red-700" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</button>
          </form>
          <div className="mt-8 flex items-start gap-3 rounded-lg border border-slate-200 bg-[#F4F6F2] p-4 text-sm text-slate-600">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <span>New to Vinsafe? <Link to="/register" className="font-semibold text-[#C20500]" data-testid="login-register-link">Register your business</Link>. Just need a quote? <Link to="/enquiry" className="font-semibold text-[#C20500]" data-testid="login-enquiry-link">Submit a bulk enquiry</Link>.</span>
          </div>
          <Link to="/" className="mt-6 inline-block text-sm text-slate-500 hover:text-slate-900" data-testid="login-back-home">← Back to website</Link>
        </div>
      </div>
    </div>
  );
}
